import {
  AfterContentInit,
  AfterViewChecked,
  Component,
  ElementRef,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { captionize, TableColumn } from '@app/model';
import { AppMenuItem } from '../../util/menu-item';
import { ColumnResizeEvent } from '../table/column-resizer.directive';
import { ContextMenuComponent } from '../table/context-menu.component';
import {
  LocalSort,
  nextSort,
  sortMenuItems,
  sortRows,
} from '../../util/local-sort';

/** A column of a local grid. */
export interface GridColumn {
  /** The data field, also the key of the column. */
  field?: string;
  /** Key of a column without data field, e.g. an edit button column. */
  key?: string;
  caption?: string;
  dataType?: 'string' | 'number' | 'date';
  format?: TableColumn['format'];
  alignment?: 'left' | 'right' | 'center';
  width?: number;
  /** Name of the cell template (appGridCell) of the column. */
  template?: string;
  /** Name of the template (appGridCell) of the header cell. */
  headerTemplate?: string;
  /** The value to sort by instead of the value of the field. */
  sortValue?: (row: any) => unknown; // eslint-disable-line @typescript-eslint/no-explicit-any
  sortable?: boolean;
}

/** A cell template of a local grid: `<ng-template appGridCell="name" let-row>`. */
@Directive({
  selector: '[appGridCell]',
  standalone: false,
})
export class GridCellDirective {
  template = inject<TemplateRef<unknown>>(TemplateRef);

  @Input('appGridCell') name = '';
}

type ShownColumn = GridColumn & {
  id: string;
  header: string;
  align: 'left' | 'right' | 'center';
  shownWidth?: number;
  /** Width of a content sized column in a grid without room, see fitColumns. */
  fitWidth?: number;
};

/**
 * A data grid with local data as the company pages use it: sorting by header
 * click or header context menu, alternating rows, word wrap, 20 rows per page
 * with a pager of page numbers, resizable columns and optionally an edit button
 * per row.
 */
@Component({
  selector: 'app-local-grid',
  templateUrl: './local-grid.component.html',
  styleUrls: ['./local-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocalGridComponent
  implements OnChanges, AfterContentInit, AfterViewChecked, OnDestroy
{
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('headerMenu') headerMenu?: ContextMenuComponent;
  @ContentChildren(GridCellDirective)
  cellTemplates?: QueryList<GridCellDirective>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() rows: any[] | null = [];
  @Input() columns: GridColumn[] = [];
  @Input() showHeaders = true;
  @Input() wordWrap = true;
  /** Alternating row colours (rowAlternationEnabled). */
  @Input() striped = true;
  @Input() resizable = true;
  @Input() reorderable = false;
  /** Adds the column with the edit button of each row. */
  @Input() editable = false;
  /** On iOS the grids had fixed 100px columns. */
  @Input() columnWidth: number | undefined;
  @Input() pageSize = 20;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() edit = new EventEmitter<any>();

  public shown: ShownColumn[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public sortedRows: any[] = [];
  public sort: LocalSort | null = null;
  /** The index of the first row of the shown page. */
  public first = 0;
  /** A header drag is on, so the click that ends it does not sort. */
  public dragging = false;
  public headerMenuItems: AppMenuItem[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public templates: { [name: string]: TemplateRef<any> } = {};
  private widths: { [id: string]: number } = {};
  /** Widths of the content sized columns, see fitColumns. */
  private fitWidths: { [id: string]: number } = {};
  private fitPending = true;
  /** Whether the grid sizes its columns by content, see fitColumns. */
  public fitted = false;
  private fitWidth = 0;
  private resizeObserver?: ResizeObserver;

  ngAfterViewChecked() {
    if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.host.nativeElement.offsetWidth !== this.fitWidth) {
          this.zone.run(() => this.fitColumns());
        }
      });
      this.resizeObserver.observe(this.host.nativeElement);
    }
    if (this.fitPending) {
      this.fitPending = false;
      // After the rendering, as it measures the rendered table.
      Promise.resolve().then(() => this.zone.run(() => this.fitColumns()));
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  /**
   * Automatic column widths: every column is measured with its content on one
   * line (best fit), and the columns without a fixed width share the room left
   * by these best fit widths, scaled up or down; texts that do not fit wrap.
   * Grids with a fixed width for a data column are left to the table and may
   * overflow.
   */
  fitColumns() {
    // It runs after the rendering, from a promise or the resize observer.
    this.changeDetector.markForCheck();
    const host = this.host.nativeElement;
    const table = host.querySelector<HTMLElement>('table');
    this.fitWidth = host.offsetWidth;
    this.fitted = !this.shown.some(
      (column) => column.field && column.width !== undefined
    );
    if (!table || !host.offsetWidth || !this.fitted) {
      return;
    }
    const measure = () =>
      Array.from(
        table.querySelectorAll<HTMLElement>('thead th[data-column]')
      ).map((th) => ({
        id: th.getAttribute('data-column') as string,
        width: th.offsetWidth,
      }));
    host.classList.add('best-fit');
    host.classList.toggle('best-fit-unbounded', !this.wordWrap);
    const bestFit = measure();
    const others = Array.from(
      table.querySelectorAll<HTMLElement>('thead th:not([data-column])')
    ).reduce((sum, th) => sum + th.offsetWidth, 0);
    // The narrowest each column can get with wrapped texts. Without word wrap
    // that is the width on one line: the grid then scrolls horizontally instead
    // of narrowing its columns.
    host.classList.add('min-fit');
    const minFit = this.wordWrap ? measure() : bestFit;
    host.classList.remove('best-fit', 'best-fit-unbounded', 'min-fit');

    const fixed = this.shown.filter(
      (column) => column.shownWidth !== undefined
    );
    const auto = bestFit.filter((x) => !fixed.some((y) => y.id === x.id));
    const room =
      host.offsetWidth -
      others -
      fixed.reduce((sum, column) => sum + (column.shownWidth as number), 0);
    const fitWidths = fitColumnWidths(auto, minFit, room);
    if (JSON.stringify(fitWidths) !== JSON.stringify(this.fitWidths)) {
      this.fitWidths = fitWidths;
      this.shown = this.shown.map((column) => ({
        ...column,
        fitWidth: this.fitWidths[column.id],
      }));
    }
  }

  ngAfterContentInit() {
    this.templates = (this.cellTemplates?.toArray() || []).reduce(
      (acc, cell) => ({ ...acc, [cell.name]: cell.template }),
      {}
    );
  }

  ngOnChanges() {
    this.shown = this.columns.map((column, index) =>
      this.toShown(column, index)
    );
    this.sortedRows = this.sortRows();
    if (this.first >= this.sortedRows.length) {
      this.first = 0;
    }
    this.fitPending = true;
  }

  /** The rows of the shown page. */
  get pageRows() {
    return this.sortedRows.slice(this.first, this.first + this.pageSize);
  }

  private toShown(column: GridColumn, index: number): ShownColumn {
    const id = column.field || column.key || 'column' + index;
    return {
      ...column,
      id,
      header:
        column.caption !== undefined
          ? column.caption
          : column.field
            ? captionize(column.field)
            : '',
      align:
        column.alignment || (column.dataType === 'number' ? 'right' : 'left'),
      shownWidth: this.widths[id] ?? column.width ?? this.columnWidth,
      sortable: column.sortable ?? !!column.field,
    };
  }

  private sortRows() {
    const rows = this.rows || [];
    const sort = this.sort;
    if (!sort) {
      return rows;
    }
    const column = this.shown.find((x) => x.id === sort.field);
    const valueOf = column?.sortValue || ((row: never) => row[sort.field]);
    // Sort by the sort value, then back to the rows.
    return sortRows(
      rows.map((row) => ({ row, value: valueOf(row as never) })),
      { field: 'value', order: sort.order }
    ).map((x) => x.row);
  }

  onHeaderClick(event: Event, column: ShownColumn) {
    if (column.sortable && !this.dragging) {
      this.applySort(nextSort(this.sort, column.id, event));
    }
  }

  onHeaderContextMenu(event: MouseEvent, column: ShownColumn) {
    if (column.sortable && this.headerMenu) {
      this.headerMenuItems = sortMenuItems(column.id, (sort) =>
        this.applySort(sort)
      );
      this.headerMenu.show(event);
    }
  }

  // A new sorting starts on the first page.
  private applySort(sort: LocalSort | null) {
    this.sort = sort;
    this.sortedRows = this.sortRows();
    this.first = 0;
  }

  /**
   * The widths are kept per column, so they move with the columns. The next
   * column gives or takes the difference, the table keeps its width.
   */
  onColumnResize(event: ColumnResizeEvent) {
    const headers: HTMLElement[] = Array.from(
      this.host.nativeElement.querySelectorAll('thead th[data-column]')
    );
    const resized = headers.indexOf(event.element);
    const next = headers[resized + 1];
    // Both columns stay as they are when one would get 15px or less.
    if (
      resized < 0 ||
      event.element.offsetWidth + event.delta <= 15 ||
      (next && next.offsetWidth - event.delta <= 15)
    ) {
      return;
    }
    headers.forEach((th, index) => {
      const id = th.getAttribute('data-column') as string;
      const shown = this.shown.find((x) => x.id === id);
      const change =
        index === resized
          ? event.delta
          : index === resized + 1
            ? -event.delta
            : 0;
      // Columns with a width keep it as minimum width of their cells, so the
      // resized column and its neighbour follow from that width; the others
      // keep the width they have.
      const width =
        change && shown?.shownWidth !== undefined
          ? shown.shownWidth
          : th.offsetWidth;
      this.widths[id] = Math.max(15, Math.round(width + change));
    });
    this.shown = this.shown.map((column) => ({
      ...column,
      shownWidth: this.widths[column.id] ?? column.shownWidth,
    }));
  }

  onColumnDrop(event: CdkDragDrop<unknown>) {
    const shown = [...this.shown];
    moveItemInArray(shown, event.previousIndex, event.currentIndex);
    this.shown = shown;
  }

  // The click at the end of a drag comes after cdkDragEnded.
  onColumnDragEnded() {
    setTimeout(() => (this.dragging = false));
  }

  isAltRow(rowIndex: number) {
    return rowIndex % 2 === 1;
  }
}

interface MeasuredColumn {
  id: string;
  width: number;
}

/**
 * The widths of the columns sized by content, from their widths with the
 * content on one line (bestFit) and wrapped (minFit), for the given room.
 * Like the old grid the best fit widths are scaled to the room, as long as no
 * column gets narrower than its wrapped content. Otherwise like the browser's
 * auto table layout: each column gets its narrowest width plus a share of the
 * rest in proportion to what it lacks.
 */
export function fitColumnWidths(
  bestFit: MeasuredColumn[],
  minFit: MeasuredColumn[],
  room: number
): { [id: string]: number } {
  const needed = bestFit.reduce((sum, x) => sum + x.width, 0);
  const widths: { [id: string]: number } = {};
  if (needed <= 0 || room <= 0) {
    return widths;
  }
  const narrowest = (x: MeasuredColumn) =>
    Math.min(minFit.find((y) => y.id === x.id)?.width ?? 0, x.width);
  const least = bestFit.reduce((sum, x) => sum + narrowest(x), 0);
  const scaled = !bestFit.some((x) => (x.width * room) / needed < narrowest(x));
  bestFit.forEach((x) => {
    const min = narrowest(x);
    widths[x.id] = Math.floor(
      scaled
        ? (x.width * room) / needed
        : room <= least
          ? min
          : min + ((x.width - min) * (room - least)) / (needed - least)
    );
  });
  return widths;
}
